/**
 * Merkt den entsperrten Schlüssel auf diesem Gerät, damit nicht jedes Neuladen
 * die Master-Passphrase verlangt.
 *
 * Warum IndexedDB und nicht sessionStorage:
 * In IndexedDB lässt sich ein `CryptoKey` als Objekt ablegen. Bleibt er dabei
 * **nicht auslesbar** (`extractable: false`), hält der Browser das
 * Schlüsselmaterial selbst - JavaScript kann damit entschlüsseln, aber die
 * Bytes nie zu Gesicht bekommen. In sessionStorage läge der Schlüssel im
 * Klartext und wäre mit einer Zeile auszulesen.
 *
 * Was das trotzdem nicht leistet:
 * Wer Schadcode auf der Seite ausführen kann, kann den Schlüssel *benutzen*,
 * solange er gültig ist - nur eben nicht mitnehmen. Und wer an das entsperrte
 * Gerät kommt, kommt an den Tresor. Deshalb ist das Merken abschaltbar,
 * zeitlich begrenzt und wird beim Sperren und Abmelden sofort verworfen.
 */

const DB_NAME = 'locker-keystore';
const STORE = 'keys';
const RECORD_ID = 'vault-key';

export type RememberChoice = 'never' | 'session' | '8h' | '24h' | '7d';

export const REMEMBER_LABEL: Record<RememberChoice, string> = {
  never: 'Jedes Mal fragen',
  session: 'Bis der Browser geschlossen wird',
  '8h': '8 Stunden',
  '24h': '24 Stunden',
  '7d': '7 Tage',
};

/** Wie lange die Wahl gilt. `session` bekommt eine großzügige Obergrenze. */
function durationMs(choice: RememberChoice): number {
  switch (choice) {
    case 'never':
      return 0;
    case 'session':
      return 16 * 3600_000;
    case '8h':
      return 8 * 3600_000;
    case '24h':
      return 24 * 3600_000;
    case '7d':
      return 7 * 24 * 3600_000;
  }
}

interface StoredKey {
  id: string;
  key: CryptoKey;
  uid: string;
  expiresAt: number;
  /** Kennzeichnet Einträge, die beim Schließen des Browsers hinfällig werden. */
  sessionOnly: boolean;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Der Schlüssel darf nur gemerkt werden, wenn er nicht auslesbar ist - sonst
 * wäre der ganze Vorteil gegenüber sessionStorage dahin.
 */
export async function rememberKey(
  key: CryptoKey,
  uid: string,
  choice: RememberChoice,
): Promise<void> {
  if (choice === 'never') {
    await forgetKey();
    return;
  }
  if (key.extractable) {
    throw new Error('Auslesbare Schlüssel werden nicht gemerkt.');
  }

  const record: StoredKey = {
    id: RECORD_ID,
    key,
    uid,
    expiresAt: Date.now() + durationMs(choice),
    sessionOnly: choice === 'session',
  };

  try {
    await withStore('readwrite', (store) => store.put(record));
    if (choice === 'session') sessionStorage.setItem('locker-session-mark', '1');
  } catch {
    // Im privaten Modus mancher Browser ist IndexedDB gesperrt. Dann wird eben
    // jedes Mal gefragt - kein Grund, die Anmeldung scheitern zu lassen.
  }
}

/** Liefert den gemerkten Schlüssel, sofern er noch gilt und zur Person passt. */
export async function recallKey(uid: string): Promise<CryptoKey | null> {
  let record: StoredKey | undefined;
  try {
    record = await withStore<StoredKey | undefined>('readonly', (store) => store.get(RECORD_ID));
  } catch {
    return null;
  }
  if (!record) return null;

  const abgelaufen = record.expiresAt < Date.now();
  const andereSitzung = record.sessionOnly && sessionStorage.getItem('locker-session-mark') !== '1';
  const anderePerson = record.uid !== uid;

  if (abgelaufen || andereSitzung || anderePerson) {
    await forgetKey();
    return null;
  }
  return record.key;
}

export async function forgetKey(): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.delete(RECORD_ID));
  } catch {
    // Nichts zu löschen ist kein Fehler.
  }
  sessionStorage.removeItem('locker-session-mark');
}

/** Bis wann der gemerkte Schlüssel gilt - für die Anzeige in den Einstellungen. */
export async function rememberedUntil(): Promise<Date | null> {
  try {
    const record = await withStore<StoredKey | undefined>('readonly', (store) =>
      store.get(RECORD_ID),
    );
    if (!record || record.expiresAt < Date.now()) return null;
    return new Date(record.expiresAt);
  } catch {
    return null;
  }
}

const CHOICE_KEY = 'locker-remember-choice';

export function savedChoice(): RememberChoice {
  const value = localStorage.getItem(CHOICE_KEY);
  return value && value in REMEMBER_LABEL ? (value as RememberChoice) : 'never';
}

export function saveChoice(choice: RememberChoice): void {
  localStorage.setItem(CHOICE_KEY, choice);
}
